import { CubeFace, LODLevel, TileConfig } from '../Types';
import { UrlBuilder } from '../tile-manager/UrlBuilder';
import { TimelapseSequenceConfig, TimelapseSequenceFrame, TimelapseFrameReadiness } from './TimelapseSequenceTypes';

/**
 * Manages timelapse frames for the 360 viewer, handling preload/prefetch rules.
 *
 * - Prefetches a configurable number of frames (defaults to 5).
 * - When the active index reaches the third frame inside the current window,
 *   the manager rolls the window forward and preloads the next batch.
 * - Prefers tile preloading when metadata exists; otherwise falls back to the
 *   progressive single-image flow (thumbnail → full image).
 */
export class TimelapseSequenceManager {
  private frames: TimelapseSequenceFrame[] = [];
  private readonly config: Required<TimelapseSequenceConfig>;
  private prefetchWindowStart = 0;
  private readonly prefetchedFrames = new Map<string, number>(); // frameId -> timestamp
  private readonly inflightPrefetches = new Map<string, Promise<void>>();
  private readonly imageCache = new Map<string, HTMLImageElement>(); // url -> preloaded image

  constructor(config?: TimelapseSequenceConfig) {
    const prefetchAheadCount = config?.prefetchAheadCount ?? 5; // Changed from 10 to 5 for chunk-based loading
    this.config = {
      prefetchAheadCount,
      warmupCount: config?.warmupCount ?? prefetchAheadCount,
    };
  }

  /**
   * Reset the manager and cancel any tracked prefetch metadata.
   */
  public clear(): void {
    this.frames = [];
    this.prefetchedFrames.clear();
    this.inflightPrefetches.clear();
    this.imageCache.clear();
    this.prefetchWindowStart = 0;
  }

  /**
   * Replace the managed frame sequence and prefetch an initial window.
   */
  public setFrames(frames: TimelapseSequenceFrame[], startIndex = 0): void {
    this.frames = frames ?? [];
    this.prefetchWindowStart = Math.max(0, startIndex);
    this.prefetchedFrames.clear();
    this.inflightPrefetches.clear();

    if (this.frames.length === 0) {
      return;
    }

    const warmupStart = Math.max(0, startIndex);
    const warmupEnd = Math.min(this.frames.length - 1, warmupStart + this.config.warmupCount - 1);
    this.prefetchRange(warmupStart, warmupEnd);
  }

  /**
   * Ensure the frame at `index` (and the configured ahead window) is prefetched.
   */
  public ensureFrameReady(index: number): void {
    if (!this.frames.length || index < 0 || index >= this.frames.length) {
      return;
    }

    const end = Math.min(this.frames.length - 1, index + this.config.prefetchAheadCount - 1);
    this.prefetchRange(index, end);
  }

  /**
   * Advance the prefetch window. When the caller reaches the third frame inside
   * the current window, the manager loads the next window automatically.
   */
  public advanceWindow(currentIndex: number): void {
    if (!this.frames.length) {
      return;
    }

    const relativeIndex = currentIndex - this.prefetchWindowStart;
    if (relativeIndex < 3) {  // Changed from 4 to 3 for earlier preloading
      return;
    }

    const nextWindowStart = Math.min(
      this.frames.length - 1,
      currentIndex + 1,
    );
    const nextWindowEnd = Math.min(
      this.frames.length - 1,
      nextWindowStart + this.config.prefetchAheadCount - 1,
    );

    if (nextWindowStart <= this.prefetchWindowStart) {
      return;
    }

    this.prefetchWindowStart = nextWindowStart;
    this.prefetchRange(nextWindowStart, nextWindowEnd);
  }

  /**
   * Get the frame descriptor at the provided index.
   */
  public getFrame(index: number): TimelapseSequenceFrame | undefined {
    return this.frames[index];
  }

  /**
   * Snapshot of prefetch state (useful for diagnostics).
   */
  public getReadinessSnapshot(): TimelapseFrameReadiness[] {
    return this.frames.map((frame) => ({
      id: frame.id,
      hasTiles: Array.isArray(frame.tiles) && frame.tiles.length > 0,
      isPrefetched: this.prefetchedFrames.has(frame.id),
      lastPrefetchedAt: this.prefetchedFrames.get(frame.id),
    }));
  }

  /**
   * Prefetch a range of frames, inclusive.
   */
  private prefetchRange(startIndex: number, endIndex: number): void {
    if (startIndex > endIndex) {
      return;
    }

    for (let index = startIndex; index <= endIndex; index++) {
      const frame = this.frames[index];
      if (!frame || this.prefetchedFrames.has(frame.id)) {
        continue;
      }

      if (this.inflightPrefetches.has(frame.id)) {
        continue;
      }

      const prefetchPromise = this.prefetchFrame(frame)
        .then(() => {
          this.prefetchedFrames.set(frame.id, Date.now());
        })
        .catch(() => {
          this.prefetchedFrames.delete(frame.id);
        })
        .finally(() => {
          this.inflightPrefetches.delete(frame.id);
        });

      this.inflightPrefetches.set(frame.id, prefetchPromise);
    }
  }

  /**
   * Prefetch a single frame by either warming tile faces or preloading the thumbnail/full image.
   */
  private async prefetchFrame(frame: TimelapseSequenceFrame): Promise<void> {
    if (frame.tiles && frame.tiles.length > 0) {
      await this.prefetchTiles(frame);
      return;
    }

    const progressiveUrl = frame.thumbnailUrl || frame.imageUrl;
    if (progressiveUrl) {
      await this.loadImage(progressiveUrl);
    }
  }

  /**
   * Prefetch the lowest LOD cube-face tiles so the TileManager can reuse the browser cache.
   */
  private async prefetchTiles(frame: TimelapseSequenceFrame): Promise<void> {
    if (!frame.imageUrl || !frame.tiles || frame.tiles.length === 0) {
      return;
    }

    const tileConfig: TileConfig = {
      url: frame.imageUrl,
      levels: frame.tiles,
    };

    const lowestLevel = [...frame.tiles]
      .sort((a, b) => a.level - b.level)[0];

    if (!lowestLevel) {
      return;
    }

    const lodLevels: LODLevel[] = frame.tiles!.map((level) => ({
      level: level.level,
      tilesPerFace: level.cols_per_face,
      fovThreshold: { min: 0, max: 180 },
      priority: 0,
    }));

    const preloadTasks = Object.values(CubeFace).map((face) => {
      const tileUrl = UrlBuilder.build(
        { face: face as CubeFace, level: lowestLevel.level, x: 0, y: 0 },
        tileConfig,
        lodLevels,
      );

      return this.loadImage(tileUrl);
    });

    await Promise.allSettled(preloadTasks);
  }

  /**
   * Preload an image by instantiating an HTMLImageElement and resolving when it completes.
   * Uses a cache to avoid re-loading the same images.
   */
  private loadImage(url: string): Promise<void> {
    // Check if image is already cached
    if (this.imageCache.has(url)) {
      return Promise.resolve();
    }
    
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Store in cache on successful load
        this.imageCache.set(url, img);
        resolve();
      };
      img.onerror = () => resolve();
      img.src = url;
    });
  }
}


